<template>
  <div class="create-template-wrapper">
    <h2>Create Course Template</h2>

    <div class="stepper-header">
      <div class="stepper-step" :class="{active: currentStep === 1, done: currentStep > 1}">
        <span class="stepper-num" :class="{clickable: currentStep > 1}" @click="currentStep > 1 ? goToStep(1) : null">1</span>
        <span class="stepper-label">Basic Info</span>
      </div>
      <div class="stepper-connector" :class="{done: currentStep > 1}"></div>
      <div class="stepper-step" :class="{active: currentStep === 2, done: currentStep > 2}">
        <span class="stepper-num" :class="{clickable: currentStep > 2}" @click="currentStep > 2 ? goToStep(2) : null">2</span>
        <span class="stepper-label">Products</span>
      </div>
      <div class="stepper-connector" :class="{done: currentStep > 2}"></div>
      <div class="stepper-step" :class="{active: currentStep === 3}">
        <span class="stepper-num">3</span>
        <span class="stepper-label">Round Config</span>
      </div>
    </div>

    <!-- Step 1: Basic Info -->
    <div v-if="currentStep === 1">
      <div class="builder-grid">
        <md-field :class="{ 'md-invalid': showTemplateMetaValidation && !nameTrimmed }">
          <label>Template Name*</label>
          <md-input v-model="name"></md-input>
        </md-field>

        <md-field :class="{ 'md-invalid': showTemplateMetaValidation && !descriptionTrimmed }">
          <label>Description*</label>
          <md-input v-model="description"></md-input>
        </md-field>

        <md-field>
          <label>Min Teams</label>
          <md-input type="number" min="1" v-model.number="minTeams"></md-input>
        </md-field>

        <md-field>
          <label>Max Teams</label>
          <md-input type="number" min="1" max="16" v-model.number="maxTeams"></md-input>
        </md-field>

        <md-field>
          <label>Min Students Per Team</label>
          <md-input type="number" min="1" v-model.number="minStudentsPerTeam"></md-input>
        </md-field>

        <md-field>
          <label>Max Students Per Team</label>
          <md-input type="number" min="1" max="16" v-model.number="maxStudentsPerTeam"></md-input>
        </md-field>

        <md-field>
          <label>Round Count</label>
          <md-input type="number" v-model.number="roundCount"></md-input>
        </md-field>
      </div>
      <div v-if="saveValidationError" class="error pre-round-error">{{saveValidationError}}</div>
      <div class="actions-row step-nav-row">
        <md-button class="md-raised btn-primary" @click="goToStep2">Next →</md-button>
      </div>
    </div>

    <!-- Step 2: Products -->
    <div v-if="currentStep === 2">
      <div class="products-builder">
        <div class="round-section-title">Products (we reccomend using 3)</div>
        <div v-for="(product, i) in products" :key="`product-item-${i}`" class="product-item-row">
          <input class="product-add-input product-edit-input"
                 :value="product"
                 @input="products.splice(i, 1, $event.target.value)"
                 maxlength="40"
                 placeholder="Product name" />
          <button class="product-remove-btn" @click="removeProduct(i)" :disabled="products.length <= 1" title="Remove product">✕</button>
        </div>
        <div class="product-add-row">
          <input class="product-add-input" v-model="newProductText" placeholder="New product name" @keyup.enter="addProduct" maxlength="40" />
          <md-button class="md-raised product-add-btn" @click="addProduct">+</md-button>
        </div>
        <div v-if="productAddError" class="error product-add-error">{{productAddError}}</div>
      </div>
      <div v-if="saveValidationError" class="error pre-round-error">{{saveValidationError}}</div>
      <div class="actions-row step-nav-row">
        <md-button class="md-raised" @click="goToStep(1)">← Back</md-button>
        <md-button class="md-raised btn-primary" @click="goToStep3">Next →</md-button>
      </div>
    </div>

    <!-- Step 3: Round Config -->
    <div v-if="currentStep === 3">

    <div v-if="hasSavedRoundSetup" class="same-config-row">
      <label class="toggle-label">
        <input type="checkbox" v-model="sameConfigEachRound">
        Same config each round
      </label>
      <label class="toggle-label" style="margin-left: 1.5em;">
        <input type="checkbox" v-model="randomizeDemand">
        Randomize demand values for each new game
      </label>
    </div>

    <template v-for="(editor, index) in perRoundEditors">
    <div class="rounds-section" v-if="!sameConfigEachRound || index === 0" :key="`round-editor-${index}`">
      <div class="round-header">
        <h3>
          {{sameConfigEachRound ? 'Configure (applied to all rounds)' : `Round ${index + 1}`}}
          <span v-if="!sameConfigEachRound && roundValidationStates[index]" class="round-complete-check">✓</span>
        </h3>
        <md-button class="md-raised" :disabled="!hasSavedRoundSetup" @click="toggleRoundCollapsed(index)">
          {{roundCollapsedStates[index] ? 'Expand' : 'Collapse'}}
        </md-button>
      </div>

      <div v-if="!roundCollapsedStates[index]">

        <div class="round-section-title">Products</div>
        <details v-for="(product, productIndex) in editor.roundObj.products"
                 :key="`product-${index}-${productIndex}`"
                 class="advanced-section product-details">
          <summary class="advanced-summary">
            <span class="advanced-summary-arrow">▾</span>
            <span>{{lockedProductName(productIndex) || `Product ${productIndex + 1}`}}</span>
          </summary>
          <div class="round-inputs-grid">
            <md-field>
              <label>Game Product</label>
              <md-input :value="lockedProductName(productIndex)" disabled></md-input>
            </md-field>
            <md-field>
              <label>Demand Slope</label>
              <md-input type="number" step="0.01" v-model.number="product.demandSlope" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Production Cost</label>
              <md-input type="number" step="0.01" v-model.number="product.productionCost" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Demand Intercept</label>
              <md-input type="number" step="0.01" v-model.number="product.demandIntercept" @input="syncEditorTexts(index)"></md-input>
            </md-field>
          </div>
        </details>

        <div class="round-section-title">Financial Submissions</div>
        <div class="financial-submissions-note">Choose when each statement is visible or required for students.</div>
        <div class="round-inputs-grid financial-submissions-grid">
          <div class="financial-field">
            <div class="financial-label">Cash Flow</div>
            <select class="financial-select" v-model="editor.featureObj.cashFlowConfig.roundSetting" @change="syncEditorTexts(index)">
              <option value="SUBMIT">Required</option>
              <option value="SHOW_FOR_ALL_ROUNDS">Show In Current + Past Rounds</option>
              <option value="SHOW_FOR_PAST_ROUNDS">Show Only In Past Rounds</option>
              <option value="OFF">Off</option>
            </select>
          </div>
          <div class="financial-field">
            <div class="financial-label">Balance Sheet</div>
            <select class="financial-select" v-model="editor.featureObj.balanceSheetConfig.roundSetting" @change="syncEditorTexts(index)">
              <option value="SUBMIT">Required</option>
              <option value="SHOW_FOR_ALL_ROUNDS">Show In Current + Past Rounds</option>
              <option value="SHOW_FOR_PAST_ROUNDS">Show Only In Past Rounds</option>
              <option value="OFF">Off</option>
            </select>
          </div>
          <div class="financial-field">
            <div class="financial-label">Income Statement</div>
            <select class="financial-select" v-model="editor.featureObj.incomeStatementConfig.roundSetting" @change="syncEditorTexts(index)">
              <option value="SUBMIT">Required</option>
              <option value="SHOW_FOR_ALL_ROUNDS">Show In Current + Past Rounds</option>
              <option value="SHOW_FOR_PAST_ROUNDS">Show Only In Past Rounds</option>
              <option value="OFF">Off</option>
            </select>
          </div>
          <div class="financial-field">
            <div class="financial-label">Journal Entry</div>
            <select class="financial-select" v-model="editor.featureObj.journalEntryTAccountConfig" @change="syncEditorTexts(index)">
              <option value="SHOW_FOR_ALL_ROUNDS">Show In Current + Past Rounds</option>
              <option value="SHOW_FOR_PAST_ROUNDS">Show Only In Past Rounds</option>
              <option value="OFF">Off</option>
            </select>
          </div>
        </div>

        <div class="simple-toggles-section">
          <label class="toggle-label">
            <input type="checkbox" v-model="editor.bondsEnabled" @change="onBondsEnabledChange(index)">
            Bonds enabled?
          </label>
          <label class="toggle-label" :class="{ 'toggle-disabled': index === 0 && !sameConfigEachRound }">
            <input type="checkbox" v-model="editor.roundObj.isStockTradingEnabled"
                   :disabled="index === 0 && !sameConfigEachRound"
                   @change="syncEditorTexts(index)">
            Stock trading enabled?
            <span v-if="index === 0 && !sameConfigEachRound" class="toggle-note">(round 1 starts with ipo)</span>
          </label>
          <label class="toggle-label">
            <input type="checkbox" v-model="editor.advertisingEnabled" @change="onAdvertisingEnabledChange(index)">
            Advertising cost enabled?
          </label>
          <label class="toggle-label">
            <input type="checkbox" v-model="editor.incomeTaxEnabled" @change="onIncomeTaxEnabledChange(index)">
            Income tax enabled?
          </label>
        </div>

        <details class="advanced-section">
          <summary class="round-section-title advanced-summary">
            <span class="advanced-summary-arrow">▾</span>
            <span>Configure Land</span>
          </summary>
          <div class="round-inputs-grid">
            <md-field>
              <label>Land Purchase Cost</label>
              <md-input type="number" v-model.number="editor.roundObj.lands[0].purchaseCost" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Land Building Capacity</label>
              <md-input type="number" v-model.number="editor.roundObj.lands[0].buildingCapacity" @input="syncEditorTexts(index)"></md-input>
            </md-field>
          </div>
        </details>

        <details class="advanced-section">
          <summary class="round-section-title advanced-summary">
            <span class="advanced-summary-arrow">▾</span>
            <span>Configure Buildings</span>
          </summary>
          <div class="round-inputs-grid">
            <md-field>
              <label>Building Useful Life</label>
              <md-input type="number" v-model.number="editor.roundObj.buildings[0].typesConfig[0].usefulLife" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Building Type</label>
              <md-input v-model="editor.roundObj.buildings[0].typesConfig[0].buildingType" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Building Purchase Cost</label>
              <md-input type="number" v-model.number="editor.roundObj.buildings[0].typesConfig[0].purchaseCost" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Building Equipment Capacity</label>
              <md-input type="number" v-model.number="editor.roundObj.buildings[0].typesConfig[0].equipmentCapacity" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Building Depreciation Methods (comma-separated)</label>
              <md-input v-model="editor.roundMeta.buildingDepreciationMethodsText" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Building Default Depreciation Method</label>
              <md-input v-model="editor.roundObj.buildings[0].typesConfig[0].defaultDepreciationMethod" @input="syncEditorTexts(index)"></md-input>
            </md-field>
          </div>
        </details>

        <details class="advanced-section">
          <summary class="round-section-title advanced-summary">
            <span class="advanced-summary-arrow">▾</span>
            <span>Configure Equipments</span>
          </summary>
          <div
            v-for="(eq, eqIndex) in editor.roundObj.equipments"
            :key="`equipment-${index}-${eqIndex}`"
            class="equipment-card"
          >
            <div class="equipment-card-header">
              <span>Equipment {{eqIndex + 1}}</span>
              <md-button v-if="editor.roundObj.equipments.length > 1" class="md-raised md-accent equipment-remove-btn" @click="removeEquipment(index, eqIndex)">Remove</md-button>
            </div>
            <div class="round-inputs-grid">
              <md-field>
                <label>Useful Life</label>
                <md-input type="number" v-model.number="eq.typesConfig[0].usefulLife" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Equipment Type</label>
                <md-input v-model="eq.typesConfig[0].equipmentType" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Purchase Cost</label>
                <md-input type="number" v-model.number="eq.typesConfig[0].purchaseCost" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Production Capacity</label>
                <md-input type="number" v-model.number="eq.typesConfig[0].productionCapacity" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Depreciation Methods (comma-separated)</label>
                <md-input v-model="editor.roundMeta.equipmentsDepreciationMethodsTexts[eqIndex]" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Default Depreciation Method</label>
                <md-input v-model="eq.typesConfig[0].defaultDepreciationMethod" @input="syncEditorTexts(index)"></md-input>
              </md-field>
            </div>
          </div>
          <md-button class="md-raised" @click="addEquipment(index)">+ Add Equipment</md-button>
        </details>

        <details class="advanced-section">
          <summary class="round-section-title advanced-summary">
            <span class="advanced-summary-arrow">▾</span>
            <span>Advanced Config</span>
          </summary>
          <div class="round-inputs-grid">
            <template v-if="editor.bondsEnabled">
              <md-field>
                <label>Bond Real Interest Rate Factor</label>
                <md-input type="number" step="0.01" v-model.number="editor.roundObj.bonds.realInterestRateFactor" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Bond Term</label>
                <md-input type="number" v-model.number="editor.roundObj.bonds.bondOptions[0].term" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Bond Nominal Amount</label>
                <md-input type="number" v-model.number="editor.roundObj.bonds.bondOptions[0].nominalAmount" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Bond Payments/Year</label>
                <md-input type="number" v-model.number="editor.roundObj.bonds.bondOptions[0].numPaymentsPerYear" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Bond Nominal Interest Rate</label>
                <md-input type="number" step="0.01" v-model.number="editor.roundObj.bonds.bondOptions[0].nominalInterestRate" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Bond Payment Due Quarter End</label>
                <md-input type="number" v-model.number="editor.roundObj.bonds.bondOptions[0].paymentDueQuarterEnd" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Bond Round Borrow Cap</label>
                <md-input type="number" v-model.number="editor.featureObj.borrowingCaps.bondRoundBorrowCap" @input="syncEditorTexts(index)"></md-input>
              </md-field>
            </template>

            <md-field v-if="editor.incomeTaxEnabled">
              <label>Income Tax</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.incomeTax" @input="syncEditorTexts(index)"></md-input>
            </md-field>

            <template v-if="editor.advertisingEnabled">
              <md-field>
                <label>Advertising Cost</label>
                <md-input type="number" step="0.01" v-model.number="editor.featureObj.advertising.cost" @input="syncEditorTexts(index)"></md-input>
              </md-field>
              <md-field>
                <label>Advertising Benefit</label>
                <md-input type="number" step="0.01" v-model.number="editor.featureObj.advertising.benefit" @input="syncEditorTexts(index)"></md-input>
              </md-field>
            </template>

            <div class="checkbox-row">
              <label>
                <input type="checkbox" v-model="editor.featureObj.salesReturns" @change="syncEditorTexts(index)">
                Sales Returns Enabled
              </label>
            </div>
            <md-field>
              <label>Sales Returns Lower Bound</label>
              <md-input type="number" step="0.01" v-model.number="editor.roundObj.salesReturnsPercentLowerBound" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Sales Returns Upper Bound</label>
              <md-input type="number" step="0.01" v-model.number="editor.roundObj.salesReturnsPercentUpperBound" @input="syncEditorTexts(index)"></md-input>
            </md-field>

            <md-field>
              <label>Total Principal Cap</label>
              <md-input type="number" v-model.number="editor.featureObj.borrowingCaps.totalPrincipalCap" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Debt Round Borrow Cap</label>
              <md-input type="number" v-model.number="editor.featureObj.borrowingCaps.debtRoundBorrowCap" @input="syncEditorTexts(index)"></md-input>
            </md-field>

            <md-field>
              <label>Stock Price NI Rank Factor</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.stockPriceConfig.niRankFactor" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Stock Price Net Income Factor</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.stockPriceConfig.netIncomeFactor" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Stock Price Total Assets Factor</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.stockPriceConfig.totalAssetsFactor" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Stock Price Change In Income Factor</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.stockPriceConfig.changeInIncomeFactor" @input="syncEditorTexts(index)"></md-input>
            </md-field>

            <md-field>
              <label>Accounts Receivable Bad Debt Percent</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.accountsReceivable.badDebtPercent" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Accounts Receivable Outstanding Percent</label>
              <md-input type="number" step="0.01" v-model.number="editor.featureObj.accountsReceivable.outstandingPercent" @input="syncEditorTexts(index)"></md-input>
            </md-field>

            <md-field>
              <label>Cash Flow Disabled Fields (comma-separated)</label>
              <md-input v-model="editor.featureMeta.cashFlowDisabledFieldsText" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Balance Sheet Disabled Fields (comma-separated)</label>
              <md-input v-model="editor.featureMeta.balanceSheetDisabledFieldsText" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Income Statement Disabled Fields (comma-separated)</label>
              <md-input v-model="editor.featureMeta.incomeStatementDisabledFieldsText" @input="syncEditorTexts(index)"></md-input>
            </md-field>
            <md-field>
              <label>Enabled Journal Entry Submissions (comma-separated)</label>
              <md-input v-model="editor.featureMeta.journalEntrySubmissionEnabledText" @input="syncEditorTexts(index)"></md-input>
            </md-field>

            <div class="checkbox-row">
              <label>
                <input type="checkbox" v-model="editor.featureObj.requirePositiveCashBalanceBeforeElecting" @change="syncEditorTexts(index)">
                Require Positive Cash Balance Before Electing
              </label>
            </div>
          </div>
          <md-field>
            <label>Debts JSON Array</label>
            <md-textarea v-model="editor.roundMeta.debtsJsonText" md-autogrow @input="syncEditorTexts(index)"></md-textarea>
          </md-field>
          <md-field>
            <label>Store Furnishings JSON Array</label>
            <md-textarea v-model="editor.roundMeta.storeFurnishingsJsonText" md-autogrow @input="syncEditorTexts(index)"></md-textarea>
          </md-field>
        </details>

      </div>
    </div>
    </template>

    <div v-if="hasSavedRoundSetup" class="rounds-done-row">
      <md-button class="md-raised btn-primary" :disabled="!perRoundEditors.length || allRoundsValidated" @click="collapseAllRounds">Done!</md-button>
    </div>

    <div class="actions-row">
      <md-button class="md-raised" @click="goToStep(2)">← Back</md-button>
      <md-button class="md-raised btn-primary" :disabled="httpWait" @click="submitTemplate">Create Template</md-button>
      <md-button class="md-raised" @click="generateTemplateJson">Download JSON (for debugging)</md-button>
    </div>
    <div v-if="errorMessage" class="error">{{errorMessage}}</div>
    <div v-if="successMessage" class="success">{{successMessage}}</div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'
import { MdField } from 'vue-material/dist/components'
import { MdButton } from 'vue-material/dist/components'

Vue.use(MdField)
Vue.use(MdButton)

const toArray = (count, factory) => Array.from({ length: count }, (_, index) => factory(index));

const defaultEquipmentTypeConfig = () => ({
  usefulLife: 3,
  purchaseCost: 1500,
  equipmentType: 'standard',
  productionCapacity: 1250,
  depreciationMethods: ['SL'],
  defaultDepreciationMethod: 'SL'
});

const defaultRound = (roundNum, products) => ({
  roundNum,
  bonds: {
    realInterestRateFactor: 0.04,
    bondOptions: [
      {
        term: 3,
        nominalAmount: 1000,
        numPaymentsPerYear: 1,
        nominalInterestRate: 0.04,
        paymentDueQuarterEnd: 4
      }
    ]
  },
  debts: [],
  lands: [
    {
      purchaseCost: 10000,
      buildingCapacity: 2
    }
  ],
  products: products.map((productName, index) => ({
    demandSlope: -0.75 - (index * 1.5),
    gameProduct: productName,
    productionCost: 2 + (index * 9),
    demandIntercept: 32 + (index * 48)
  })),
  buildings: [
    {
      typesConfig: [
        {
          usefulLife: 10,
          buildingType: 'standard',
          purchaseCost: 40000,
          equipmentCapacity: 3,
          depreciationMethods: ['SL'],
          defaultDepreciationMethod: 'SL'
        }
      ]
    }
  ],
  equipments: [
    {
      typesConfig: [defaultEquipmentTypeConfig()]
    }
  ],
  storeFurnishings: [],
  isStockTradingEnabled: roundNum >= 3,
  salesReturnsPercentLowerBound: 0.01,
  salesReturnsPercentUpperBound: 0.05
});

const defaultRoundFeature = (roundNum) => ({
  incomeTax: 15,
  advertising: { cost: 0, benefit: 0 },
  salesReturns: false,
  borrowingCaps: {
    totalPrincipalCap: 75000,
    bondRoundBorrowCap: roundNum === 1 ? 50000 : 75000,
    debtRoundBorrowCap: 50000
  },
  cashFlowConfig: {
    roundSetting: 'SUBMIT',
    disabledFields: []
  },
  stockPriceConfig: {
    niRankFactor: 50,
    netIncomeFactor: 0.5,
    totalAssetsFactor: 0.5,
    changeInIncomeFactor: 0.5
  },
  accountsReceivable: {
    badDebtPercent: 0.1,
    outstandingPercent: 0.1
  },
  balanceSheetConfig: {
    roundSetting: 'SUBMIT',
    disabledFields: []
  },
  incomeStatementConfig: {
    roundSetting: 'SUBMIT',
    disabledFields: []
  },
  journalEntryTAccountConfig: 'OFF',
  journalEntrySubmissionConfig: {
    enabledJournalEntrySubmissions: []
  },
  requirePositiveCashBalanceBeforeElecting: roundNum < 3
});

const parseCommaSeparatedList = (value) => {
  if(!value) {
    return [];
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeRound = (roundInput, roundNum, products) => {
  const defaults = defaultRound(roundNum, products);
  const round = roundInput || {};
  const firstBondOption = (round.bonds && Array.isArray(round.bonds.bondOptions) && round.bonds.bondOptions[0]) || {};
  const firstLand = (Array.isArray(round.lands) && round.lands[0]) || {};
  const firstBuildingType = (Array.isArray(round.buildings) && round.buildings[0] && Array.isArray(round.buildings[0].typesConfig) && round.buildings[0].typesConfig[0]) || {};

  return {
    ...defaults,
    ...round,
    bonds: {
      ...defaults.bonds,
      ...(round.bonds || {}),
      bondOptions: [
        {
          ...defaults.bonds.bondOptions[0],
          ...firstBondOption
        }
      ]
    },
    debts: Array.isArray(round.debts) ? round.debts : [],
    lands: [
      {
        ...defaults.lands[0],
        ...firstLand
      }
    ],
    products: Array.isArray(round.products) && round.products.length
      ? round.products.map((product, index) => ({
        ...(defaults.products[index] || {
          demandSlope: 0,
          gameProduct: '',
          productionCost: 0,
          demandIntercept: 0
        }),
        ...product
      }))
      : defaults.products,
    buildings: [
      {
        typesConfig: [
          {
            ...defaults.buildings[0].typesConfig[0],
            ...firstBuildingType
          }
        ]
      }
    ],
    equipments: Array.isArray(round.equipments) && round.equipments.length
      ? round.equipments.map((eq) => ({
          typesConfig: [{
            ...defaultEquipmentTypeConfig(),
            ...((eq.typesConfig && eq.typesConfig[0]) || {})
          }]
        }))
      : defaults.equipments,
    storeFurnishings: Array.isArray(round.storeFurnishings) ? round.storeFurnishings : []
  };
};

const normalizeFeature = (featureInput, roundNum) => {
  const defaults = defaultRoundFeature(roundNum);
  const feature = featureInput || {};

  return {
    ...defaults,
    ...feature,
    advertising: {
      ...defaults.advertising,
      ...(feature.advertising || {})
    },
    borrowingCaps: {
      ...defaults.borrowingCaps,
      ...(feature.borrowingCaps || {})
    },
    cashFlowConfig: {
      ...defaults.cashFlowConfig,
      ...(feature.cashFlowConfig || {}),
      disabledFields: Array.isArray(feature.cashFlowConfig && feature.cashFlowConfig.disabledFields)
        ? feature.cashFlowConfig.disabledFields
        : defaults.cashFlowConfig.disabledFields
    },
    stockPriceConfig: {
      ...defaults.stockPriceConfig,
      ...(feature.stockPriceConfig || {})
    },
    accountsReceivable: {
      ...defaults.accountsReceivable,
      ...(feature.accountsReceivable || {})
    },
    balanceSheetConfig: {
      ...defaults.balanceSheetConfig,
      ...(feature.balanceSheetConfig || {}),
      disabledFields: Array.isArray(feature.balanceSheetConfig && feature.balanceSheetConfig.disabledFields)
        ? feature.balanceSheetConfig.disabledFields
        : defaults.balanceSheetConfig.disabledFields
    },
    incomeStatementConfig: {
      ...defaults.incomeStatementConfig,
      ...(feature.incomeStatementConfig || {}),
      disabledFields: Array.isArray(feature.incomeStatementConfig && feature.incomeStatementConfig.disabledFields)
        ? feature.incomeStatementConfig.disabledFields
        : defaults.incomeStatementConfig.disabledFields
    },
    journalEntrySubmissionConfig: {
      ...defaults.journalEntrySubmissionConfig,
      ...(feature.journalEntrySubmissionConfig || {}),
      enabledJournalEntrySubmissions: Array.isArray(feature.journalEntrySubmissionConfig && feature.journalEntrySubmissionConfig.enabledJournalEntrySubmissions)
        ? feature.journalEntrySubmissionConfig.enabledJournalEntrySubmissions
        : defaults.journalEntrySubmissionConfig.enabledJournalEntrySubmissions
    }
  };
};

const buildRoundMeta = (roundObj) => ({
  buildingDepreciationMethodsText: ((roundObj.buildings[0].typesConfig[0].depreciationMethods) || []).join(', '),
  equipmentsDepreciationMethodsTexts: roundObj.equipments.map(eq => ((eq.typesConfig[0].depreciationMethods) || []).join(', ')),
  debtsJsonText: JSON.stringify(Array.isArray(roundObj.debts) ? roundObj.debts : [], null, 2),
  storeFurnishingsJsonText: JSON.stringify(Array.isArray(roundObj.storeFurnishings) ? roundObj.storeFurnishings : [], null, 2)
});

const buildFeatureMeta = (featureObj) => ({
  cashFlowDisabledFieldsText: ((featureObj.cashFlowConfig.disabledFields) || []).join(', '),
  balanceSheetDisabledFieldsText: ((featureObj.balanceSheetConfig.disabledFields) || []).join(', '),
  incomeStatementDisabledFieldsText: ((featureObj.incomeStatementConfig.disabledFields) || []).join(', '),
  journalEntrySubmissionEnabledText: ((featureObj.journalEntrySubmissionConfig.enabledJournalEntrySubmissions) || []).join(', ')
});

export default {
  name: 'CreateCourseTemplate',
  props: {
    professorId: {
      type: String,
      required: false
    },
    successCb: {
      type: Function,
      required: false
    }
  },
  data() {
    return {
      name: '',
      description: '',
      minTeams: 1,
      maxTeams: 16,
      minStudentsPerTeam: 1,
      maxStudentsPerTeam: 16,
      roundCount: 5,
      products: ['Product 1', 'Product 2', 'Product 3'],
      newProductText: '',
      productAddError: '',
      sameConfigEachRound: false,
      randomizeDemand: false,
      perRoundEditors: [],
      roundCollapsedStates: [],
      roundValidationStates: [],
      currentStep: 1,
      allRoundsValidated: false,
      hasSavedRoundSetup: false,
      showTemplateMetaValidation: false,
      saveValidationError: '',
      templateJsonCollapsed: true,
      templateJsonText: '',
      errorMessage: '',
      successMessage: '',
      httpWait: false
    }
  },
  computed: {
    nameTrimmed() {
      return (this.name || '').trim();
    },
    descriptionTrimmed() {
      return (this.description || '').trim();
    },
    hasAllBuilderFieldsFilled() {
      const hasValue = (value) => value !== '' && value !== null && value !== undefined;

      return !!(
        this.nameTrimmed
        && this.descriptionTrimmed
        && hasValue(this.minTeams)
        && hasValue(this.maxTeams)
        && hasValue(this.minStudentsPerTeam)
        && hasValue(this.maxStudentsPerTeam)
        && hasValue(this.roundCount)
        && this.products.length > 0
      );
    },
    hasInvalidBuilderValues() {
      const isValidProductText = (product) => /^[A-Za-z][A-Za-z0-9\s\-']*$/.test(product);

      return (
        !this.nameTrimmed
        || !this.descriptionTrimmed
        || Number(this.minTeams) < 1
        || Number(this.minStudentsPerTeam) < 1
        || Number(this.maxTeams) <= 0
        || Number(this.maxTeams) > 16
        || Number(this.maxStudentsPerTeam) <= 0
        || Number(this.maxStudentsPerTeam) > 16
        || !Number(this.roundCount)
        || Number(this.roundCount) < 1
        || this.products.length < 1
        || this.products.length > 10
        || this.products.some((product) => !isValidProductText(product))
      );
    },
    lockedProducts() {
      return this.products;
    }
  },
  watch: {
    roundCount() {
      this.hasSavedRoundSetup = false;
      this.saveValidationError = '';
      this.roundCollapsedStates = this.roundCollapsedStates.map(() => true);
      this.roundValidationStates = [];
      this.allRoundsValidated = false;
    },
    products: {
      deep: true,
      handler() {
        this.hasSavedRoundSetup = false;
        this.saveValidationError = '';
        this.roundCollapsedStates = this.roundCollapsedStates.map(() => true);
        this.roundValidationStates = [];
        this.allRoundsValidated = false;
      }
    },
    name() {
      this.saveValidationError = '';
    },
    description() {
      this.saveValidationError = '';
    }
  },
  methods: {
    ...mapActions([
      'importCourseTemplate'
    ]),
    validateStep1() {
      this.showTemplateMetaValidation = true;
      if (!this.nameTrimmed || !this.descriptionTrimmed) {
        this.saveValidationError = 'Template Name and Description are required';
        return false;
      }
      if (Number(this.minTeams) < 1) {
        this.saveValidationError = 'Min Teams must be at least 1';
        return false;
      }
      if (Number(this.minStudentsPerTeam) < 1) {
        this.saveValidationError = 'Min Students Per Team must be at least 1';
        return false;
      }
      if (Number(this.maxTeams) <= 0 || Number(this.maxTeams) > 16) {
        this.saveValidationError = 'Max Teams must be between 1 and 16';
        return false;
      }
      if (Number(this.maxStudentsPerTeam) <= 0 || Number(this.maxStudentsPerTeam) > 16) {
        this.saveValidationError = 'Max Students Per Team must be between 1 and 16';
        return false;
      }
      if (!Number(this.roundCount) || Number(this.roundCount) < 1) {
        this.saveValidationError = 'Round count must be at least 1';
        return false;
      }
      this.saveValidationError = '';
      return true;
    },
    goToStep(n) {
      this.saveValidationError = '';
      this.currentStep = n;
    },
    goToStep2() {
      if (this.validateStep1()) {
        this.currentStep = 2;
      }
    },
    goToStep3() {
      if (this.regeneratePerRoundEditors()) {
        this.currentStep = 3;
      }
    },
    lockedProductName(index) {
      return this.lockedProducts[index] || '';
    },
    applyLockedProductNames(editor) {
      if(!editor || !editor.roundObj || !Array.isArray(editor.roundObj.products)) {
        return;
      }

      const lockedProducts = this.lockedProducts;
      editor.roundObj.products.forEach((product, index) => {
        if(lockedProducts[index]) {
          product.gameProduct = lockedProducts[index];
        }
      });
    },
    parseProducts() {
      return this.products;
    },
    addProduct() {
      const trimmed = (this.newProductText || '').trim();
      if(!trimmed) return;
      const isValidProductText = (p) => /^[A-Za-z][A-Za-z0-9\s\-']*$/.test(p);
      if(!isValidProductText(trimmed)) {
        this.productAddError = 'Product must start with a letter and contain only letters, numbers, spaces, hyphens, or apostrophes';
        return;
      }
      if(this.products.length >= 10) {
        this.productAddError = 'Maximum 10 products allowed';
        return;
      }
      this.products.push(trimmed);
      this.newProductText = '';
      this.productAddError = '';
    },
    removeProduct(index) {
      if(this.products.length <= 1) return;
      this.products.splice(index, 1);
    },
    onBondsEnabledChange(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) return;
      if(!editor.bondsEnabled) {
        editor.featureObj.borrowingCaps.bondRoundBorrowCap = 0;
      } else {
        editor.featureObj.borrowingCaps.bondRoundBorrowCap = (index + 1) === 1 ? 50000 : 75000;
      }
      this.syncEditorTexts(index);
    },
    onAdvertisingEnabledChange(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) return;
      if(!editor.advertisingEnabled) {
        editor.featureObj.advertising.cost = 0;
        editor.featureObj.advertising.benefit = 0;
      }
      this.syncEditorTexts(index);
    },
    onIncomeTaxEnabledChange(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) return;
      editor.featureObj.incomeTax = editor.incomeTaxEnabled ? 15 : 0;
      this.syncEditorTexts(index);
    },
    regeneratePerRoundEditors(markAsSaved = true, validateOnly = false, validationAction = 'save') {
      const shouldValidate = markAsSaved || validateOnly;

      const setValidationError = (message) => {
        if(validationAction === 'save') {
          this.saveValidationError = message;
        } else {
          this.errorMessage = message;
        }
      };

      if(shouldValidate) {
        this.showTemplateMetaValidation = true;

        if(!this.nameTrimmed || !this.descriptionTrimmed) {
          setValidationError('Template Name and Description are required');
          return false;
        }

        if(Number(this.minTeams) < 1) {
          setValidationError('Min Teams must be at least 1');
          return false;
        }

        if(Number(this.minStudentsPerTeam) < 1) {
          setValidationError('Min Students Per Team must be at least 1');
          return false;
        }

        if(Number(this.maxTeams) <= 0 || Number(this.maxTeams) > 16) {
          setValidationError('Max Teams must be between 1 and 16');
          return false;
        }

        if(Number(this.maxStudentsPerTeam) <= 0 || Number(this.maxStudentsPerTeam) > 16) {
          setValidationError('Max Students Per Team must be between 1 and 16');
          return false;
        }
      }

      const products = this.products;
      const roundCount = Number(this.roundCount);
      const isValidProductText = (product) => /^[A-Za-z][A-Za-z0-9\s\-']*$/.test(product);

      if(shouldValidate && !products.length) {
        setValidationError('At least 1 product is required');
        return false;
      }

      if(shouldValidate && products.length > 10) {
        setValidationError('Products must include at most 10 values');
        return false;
      }

      if(shouldValidate && products.some((product) => !isValidProductText(product))) {
        setValidationError('Each product must be valid text (letters, numbers, spaces, hyphen, apostrophe)');
        return false;
      }

      if(shouldValidate && (!roundCount || roundCount < 1)) {
        setValidationError('Round count must be at least 1');
        return false;
      }

      this.saveValidationError = '';
      this.errorMessage = '';

      if(validateOnly) {
        return true;
      }

      this.perRoundEditors = toArray(roundCount, (index) => {
        const roundObj = normalizeRound(defaultRound(index + 1, products), index + 1, products);
        const featureObj = normalizeFeature(defaultRoundFeature(index + 1), index + 1);

        return {
          roundObj,
          featureObj,
          roundMeta: buildRoundMeta(roundObj),
          featureMeta: buildFeatureMeta(featureObj),
          roundJsonCollapsed: true,
          featureJsonCollapsed: true,
          roundJsonText: JSON.stringify(roundObj, null, 2),
          featureJsonText: JSON.stringify(featureObj, null, 2),
          bondsEnabled: true,
          advertisingEnabled: false,
          incomeTaxEnabled: true
        };
      });
      this.roundCollapsedStates = toArray(roundCount, () => true);
      this.roundValidationStates = toArray(roundCount, () => false);
      this.allRoundsValidated = false;
      this.hasSavedRoundSetup = markAsSaved;
      return true;
    },
    syncEditorTexts(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) {
        return;
      }

      this.$set(this.roundValidationStates, index, false);
      this.allRoundsValidated = false;

      this.applyLockedProductNames(editor);

      editor.roundObj.buildings[0].typesConfig[0].depreciationMethods = parseCommaSeparatedList(editor.roundMeta.buildingDepreciationMethodsText);
      editor.roundObj.equipments.forEach((eq, i) => {
        const text = (editor.roundMeta.equipmentsDepreciationMethodsTexts || [])[i] || '';
        eq.typesConfig[0].depreciationMethods = parseCommaSeparatedList(text);
      });

      try {
        const parsedDebts = JSON.parse(editor.roundMeta.debtsJsonText || '[]');
        editor.roundObj.debts = Array.isArray(parsedDebts) ? parsedDebts : [];
      } catch(err) {
        return;
      }

      try {
        const parsedStoreFurnishings = JSON.parse(editor.roundMeta.storeFurnishingsJsonText || '[]');
        editor.roundObj.storeFurnishings = Array.isArray(parsedStoreFurnishings) ? parsedStoreFurnishings : [];
      } catch(err) {
        return;
      }

      editor.featureObj.cashFlowConfig.disabledFields = parseCommaSeparatedList(editor.featureMeta.cashFlowDisabledFieldsText);
      editor.featureObj.balanceSheetConfig.disabledFields = parseCommaSeparatedList(editor.featureMeta.balanceSheetDisabledFieldsText);
      editor.featureObj.incomeStatementConfig.disabledFields = parseCommaSeparatedList(editor.featureMeta.incomeStatementDisabledFieldsText);
      editor.featureObj.journalEntrySubmissionConfig.enabledJournalEntrySubmissions = parseCommaSeparatedList(editor.featureMeta.journalEntrySubmissionEnabledText);

      editor.roundJsonText = JSON.stringify(editor.roundObj, null, 2);
      editor.featureJsonText = JSON.stringify(editor.featureObj, null, 2);
    },
    syncRoundEditorFromText(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) {
        return;
      }

      try {
        const parsed = JSON.parse(editor.roundJsonText);
        const normalized = normalizeRound(parsed, index + 1, this.parseProducts());
        editor.roundObj = normalized;
        this.applyLockedProductNames(editor);
        editor.roundMeta = buildRoundMeta(normalized);
      } catch(err) {
        return;
      }
    },
    syncFeatureEditorFromText(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) {
        return;
      }

      try {
        const parsed = JSON.parse(editor.featureJsonText);
        const normalized = normalizeFeature(parsed, index + 1);
        editor.featureObj = normalized;
        editor.featureMeta = buildFeatureMeta(normalized);
      } catch(err) {
        return;
      }
    },
    toggleRoundCollapsed(index) {
      this.$set(this.roundCollapsedStates, index, !this.roundCollapsedStates[index]);
    },
    validateRound(index) {
      const editor = this.perRoundEditors[index];
      if(!editor || !editor.roundObj || !editor.featureObj) {
        return false;
      }

      const products = editor.roundObj.products;
      if(!Array.isArray(products) || products.length === 0) {
        return false;
      }

      const areProductsValid = products.every((product) => {
        const hasName = !!(product.gameProduct && `${product.gameProduct}`.trim());
        const hasDemandSlope = Number.isFinite(Number(product.demandSlope));
        const hasProductionCost = Number.isFinite(Number(product.productionCost));
        const hasDemandIntercept = Number.isFinite(Number(product.demandIntercept));
        return hasName && hasDemandSlope && hasProductionCost && hasDemandIntercept;
      });

      if(!areProductsValid) {
        return false;
      }

      const validRoundSettingValues = ['SUBMIT', 'SHOW_FOR_ALL_ROUNDS', 'SHOW_FOR_PAST_ROUNDS', 'OFF'];
      const validJournalValues = ['SHOW_FOR_ALL_ROUNDS', 'SHOW_FOR_PAST_ROUNDS', 'OFF'];

      const isCashFlowValid = validRoundSettingValues.includes(editor.featureObj.cashFlowConfig?.roundSetting);
      const isBalanceSheetValid = validRoundSettingValues.includes(editor.featureObj.balanceSheetConfig?.roundSetting);
      const isIncomeStatementValid = validRoundSettingValues.includes(editor.featureObj.incomeStatementConfig?.roundSetting);
      const isJournalValid = validJournalValues.includes(editor.featureObj.journalEntryTAccountConfig);

      return isCashFlowValid && isBalanceSheetValid && isIncomeStatementValid && isJournalValid;
    },
    addEquipment(roundIndex) {
      const editor = this.perRoundEditors[roundIndex];
      if(!editor) return;
      editor.roundObj.equipments.push({ typesConfig: [defaultEquipmentTypeConfig()] });
      editor.roundMeta.equipmentsDepreciationMethodsTexts.push('SL');
      this.syncEditorTexts(roundIndex);
    },
    removeEquipment(roundIndex, equipIndex) {
      const editor = this.perRoundEditors[roundIndex];
      if(!editor || editor.roundObj.equipments.length <= 1) return;
      editor.roundObj.equipments.splice(equipIndex, 1);
      editor.roundMeta.equipmentsDepreciationMethodsTexts.splice(equipIndex, 1);
      this.syncEditorTexts(roundIndex);
    },
    collapseAllRounds() {
      if(this.sameConfigEachRound) {
        const isValid = this.validateRound(0);
        this.roundValidationStates = this.perRoundEditors.map(() => isValid);
        this.allRoundsValidated = isValid;
        if(isValid) {
          this.roundCollapsedStates = this.roundCollapsedStates.map(() => true);
          this.errorMessage = '';
        } else {
          this.errorMessage = 'Please complete all required Round fields before finishing.';
        }
        return;
      }

      let allValid = true;
      const nextValidationStates = this.perRoundEditors.map((_, index) => {
        const isValid = this.validateRound(index);
        if(!isValid) {
          allValid = false;
        }
        return isValid;
      });

      this.roundValidationStates = nextValidationStates;
      this.allRoundsValidated = allValid;

      if(allValid) {
        this.roundCollapsedStates = this.roundCollapsedStates.map(() => true);
        this.errorMessage = '';
      } else {
        this.errorMessage = 'Please complete all required Round fields before finishing.';
        this.roundCollapsedStates = this.roundCollapsedStates.map((_, index) => nextValidationStates[index]);
      }
    },
    toggleRoundJsonCollapsed(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) {
        return;
      }

      this.$set(editor, 'roundJsonCollapsed', !editor.roundJsonCollapsed);
    },
    toggleFeatureJsonCollapsed(index) {
      const editor = this.perRoundEditors[index];
      if(!editor) {
        return;
      }

      this.$set(editor, 'featureJsonCollapsed', !editor.featureJsonCollapsed);
    },
    toggleTemplateJsonCollapsed() {
      this.templateJsonCollapsed = !this.templateJsonCollapsed;
    },
    buildFromPerRoundEditors() {
      this.perRoundEditors.forEach((editor) => this.applyLockedProductNames(editor));

      let parsedRounds, parsedRoundFeatures;

      if(this.sameConfigEachRound && this.perRoundEditors.length > 0) {
        const baseRound = JSON.parse(this.perRoundEditors[0].roundJsonText);
        const baseFeature = JSON.parse(this.perRoundEditors[0].featureJsonText);

        parsedRounds = this.perRoundEditors.map((_, index) => {
          const r = JSON.parse(JSON.stringify(baseRound));
          r.roundNum = index + 1;
          if(index === 0) r.isStockTradingEnabled = false;
          return r;
        });

        parsedRoundFeatures = this.perRoundEditors.map(() => JSON.parse(JSON.stringify(baseFeature)));
      } else {
        parsedRounds = this.perRoundEditors.map((editor, index) => {
          try {
            return JSON.parse(editor.roundJsonText);
          } catch(err) {
            throw new Error(`Invalid JSON in Round ${index + 1} JSON`);
          }
        });

        parsedRoundFeatures = this.perRoundEditors.map((editor, index) => {
          try {
            return JSON.parse(editor.featureJsonText);
          } catch(err) {
            throw new Error(`Invalid JSON in Round ${index + 1} Feature JSON`);
          }
        });
      }

      return {
        minTeams: Number(this.minTeams),
        maxTeams: Number(this.maxTeams),
        minStudentsPerTeam: Number(this.minStudentsPerTeam),
        maxStudentsPerTeam: Number(this.maxStudentsPerTeam),
        gameProducts: this.parseProducts(),
        randomizeDemand: !!this.randomizeDemand,
        rounds: parsedRounds,
        defaultGameConfig: {
          junkLoans: {
            rate: 0.15,
            term: 3
          },
          stockConfig: {
            teamStartingStockPrice: 5,
            initialOfferingNumShares: 14000,
            totalSellableSharesPerTeam: 20000
          },
          preloadAssets: {
            debts: [],
            lands: [],
            products: [],
            buildings: [],
            equipments: [],
            storeFurnishings: []
          },
          roundFeatures: {
            incomeTax: parsedRoundFeatures.map((feature) => feature.incomeTax),
            advertising: parsedRoundFeatures.map((feature) => feature.advertising),
            salesReturns: parsedRoundFeatures.map((feature) => feature.salesReturns),
            borrowingCaps: parsedRoundFeatures.map((feature) => feature.borrowingCaps),
            cashFlowConfig: parsedRoundFeatures.map((feature) => feature.cashFlowConfig),
            stockPriceConfig: parsedRoundFeatures.map((feature) => feature.stockPriceConfig),
            accountsReceivable: parsedRoundFeatures.map((feature) => feature.accountsReceivable),
            balanceSheetConfig: parsedRoundFeatures.map((feature) => feature.balanceSheetConfig),
            incomeStatementConfig: parsedRoundFeatures.map((feature) => feature.incomeStatementConfig),
            journalEntryTAccountConfig: parsedRoundFeatures.map((feature) => feature.journalEntryTAccountConfig),
            journalEntrySubmissionConfig: parsedRoundFeatures.map((feature) => feature.journalEntrySubmissionConfig),
            requirePositiveCashBalanceBeforeElecting: parsedRoundFeatures.map((feature) => feature.requirePositiveCashBalanceBeforeElecting)
          }
        }
      };
    },
    downloadGeneratedJson(jsonText) {
      const safeName = (this.name || 'course-template').trim().replace(/\s+/g, '-').toLowerCase();
      const blob = new Blob([jsonText], { type: 'application/json' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${safeName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    },
    generateTemplateJson(shouldDownload = true, requireTemplateMeta = true) {
      try {
        if(requireTemplateMeta && !this.regeneratePerRoundEditors(false, true, 'generate')) {
          return;
        }

        this.errorMessage = '';
        const built = this.buildFromPerRoundEditors();
        const jsonText = JSON.stringify(built, null, 2);
        this.templateJsonText = jsonText;
        if(shouldDownload) {
          this.downloadGeneratedJson(jsonText);
        }
      } catch(err) {
        this.errorMessage = err.message || 'Unable to generate template JSON';
      }
    },
    async submitTemplate() {
      this.httpWait = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        if(!this.regeneratePerRoundEditors(false, true, 'submit')) {
          this.httpWait = false;
          return;
        }

        const parsedJson = this.buildFromPerRoundEditors();
        this.templateJsonText = JSON.stringify(parsedJson, null, 2);

        const createdTemplate = await this.importCourseTemplate({
          courseTemplateJSON: parsedJson,
          professorId: this.professorId,
          name: this.name,
          description: this.description
        });

        this.successMessage = `Successfully created Course Template ${this.name}`;

        if(this.successCb) {
          await this.successCb(createdTemplate);
        }
      } catch(err) {
        if(err.graphQLErrors && err.graphQLErrors.length) {
          this.errorMessage = err.graphQLErrors[0].message;
        } else if(err instanceof Error) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'Something went wrong. Please try again';
        }
      }

      this.httpWait = false;
    }
  },
  created() {
    this.regeneratePerRoundEditors(false);
    this.generateTemplateJson(false, false);
  }
}
</script>

<style scoped>

.stepper-header {
  display: flex;
  align-items: center;
  margin: 1em 0 1.75em;
}

.stepper-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3em;
}

.stepper-num {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--border-color);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  transition: background-color 0.2s, color 0.2s;
}

.stepper-step.active .stepper-num {
  background-color: var(--btn-primary-color);
  color: #fff;
}

.stepper-step.done .stepper-num {
  background-color: var(--btn-primary-color);
  color: #fff;
  opacity: 0.65;
}

.stepper-num.clickable {
  cursor: pointer;
  opacity: 1 !important;
}

.stepper-num.clickable:hover {
  opacity: 0.8 !important;
}

.stepper-label {
  font-size: 0.78rem;
  color: var(--text-primary);
  opacity: 0.65;
  white-space: nowrap;
}

.stepper-step.active .stepper-label {
  opacity: 1;
  font-weight: 600;
}

.stepper-connector {
  flex: 1;
  height: 2px;
  background-color: var(--border-color);
  margin: 0 0.5em;
  margin-bottom: 1.2em;
  transition: background-color 0.2s;
}

.stepper-connector.done {
  background-color: var(--btn-primary-color);
  opacity: 0.65;
}

.step-nav-row {
  margin-top: 1.5em;
}

h1, h2, h3, h4 {
  color: var(--text-primary);
}

.create-template-wrapper {
  background-color: var(--main-box-color);
  text-align: left;
  margin-bottom: 1.5em;
  padding: 1em;
  margin-top: 12px;
}

.builder-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 0.75em;
}

.actions-row {
  display: flex;
  gap: 0.5em;
  margin: 0.75em 0;
  flex-wrap: wrap;
}

.pre-round-actions {
  margin-top: 1em;
}

.rounds-section {
  margin-top: 1em;
  padding-top: 1em;
  border-bottom: 1px solid var(--border-color);
}

.round-section-title {
  margin: 0.75em 0 0.25em;
  font-weight: 700;
  color: var(--text-primary);
}

.round-inputs-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 0.75em;
}

.financial-submissions-note {
  margin: 0 0 0.5em;
  color: var(--text-primary);
  opacity: 0.85;
  font-size: 0.92rem;
}

.financial-submissions-grid {
  margin-bottom: 0.25em;
}

.financial-field {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.6em 0.7em 0.5em;
  background-color: var(--inner-box-color);
}

.financial-label {
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 0.35em;
}

.financial-select {
  width: 100%;
  min-height: 38px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--main-bg-color);
  color: var(--text-primary);
  padding: 0 10px;
  outline: none;
}

.financial-select:focus {
  border-color: var(--btn-primary-color);
  box-shadow: 0 0 0 1px var(--btn-primary-color);
}

.advanced-section {
  margin-top: 0.75em;
}

.advanced-section > summary {
  cursor: pointer;
  list-style: none;
}

.advanced-summary {
  display: flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.45em 0.55em;
  border-radius: 6px;
  transition: background-color 0.16s ease;
}

.advanced-summary:hover {
  background-color: var(--inner-box-color);
}

.advanced-summary-arrow {
  display: inline-block;
  width: 1em;
  text-align: center;
  transform: rotate(-90deg);
  transition: transform 0.16s ease;
}

.advanced-section[open] .advanced-summary-arrow {
  transform: rotate(0deg);
}

.advanced-section > summary::-webkit-details-marker {
  display: none;
}

.round-product-card {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.75em;
  margin-bottom: 0.75em;
}

.checkbox-row {
  display: flex;
  align-items: center;
  min-height: 48px;
  color: var(--text-primary);
}

.round-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.round-complete-check {
  color: var(--btn-primary-color);
  margin-left: 0.4em;
  font-weight: 700;
}

.rounds-done-row {
  margin-top: 1em;
  display: flex;
  justify-content: flex-start;
}

.equipment-card {
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.75em;
  margin-bottom: 0.75em;
}

.equipment-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5em;
  font-weight: 600;
  color: var(--text-primary);
}

.equipment-remove-btn {
  margin: 0;
}

.json-editor-section {
  margin-top: 0.75em;
}

.json-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.products-builder {
  margin-top: 0.75em;
}

.product-item-row {
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.3em 0.1em;
  border-bottom: 1px solid var(--border-color);
}

.product-item-row:last-of-type {
  border-bottom: none;
}

.product-item-name {
  flex: 1;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.product-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #ff1744;
  font-size: 0.85rem;
  padding: 0.15em 0.4em;
  border-radius: 3px;
  line-height: 1;
}

.product-remove-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.product-remove-btn:not(:disabled):hover {
  background-color: rgba(255, 23, 68, 0.1);
}

.product-add-row {
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-top: 0.5em;
}

.product-add-input {
  flex: 1;
  min-height: 36px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--main-bg-color);
  color: var(--text-primary);
  padding: 0 10px;
  outline: none;
  font-size: 0.9rem;
}

.product-add-input:focus {
  border-color: var(--btn-primary-color);
}

.product-add-btn {
  margin: 0;
  min-width: 40px;
}

.product-add-error {
  margin-top: 0.25em;
  font-size: 0.85rem;
}

.same-config-row {
  margin: 0.75em 0 0.5em;
}

.product-details > summary {
  padding: 0.4em 0.2em;
}

.simple-toggles-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em 1.5em;
  margin: 0.75em 0;
  padding: 0.6em 0.75em;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--inner-box-color);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.4em;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.92rem;
}

.toggle-label input[type="checkbox"] {
  cursor: pointer;
}

.toggle-disabled {
  opacity: 0.5;
}

.toggle-note {
  font-size: 0.78rem;
  opacity: 0.65;
  margin-left: 0.2em;
}

.success {
  color: var(--btn-primary-color);
}

.error {
  color: #ff1744;
}
</style>
